import { useState, useEffect } from "react";
import { getUserConfig } from "../../config/user";
import { useOptionalWidgetConfig } from "../../widget/hooks/useWidgetConfig";
import { ResidentSummaryCards } from "../molecules/ResidentSummaryCards";
import { useCurrentUser } from "../../hooks/useCurrentUser";

interface HeaderProps {
  className?: string;
  summaryData?: {
    totalCount: number;
    highRiskCount: number;
    mediumRiskCount: number;
    lowRiskCount: number;
  };
}

export function Header({ className = "", summaryData }: HeaderProps) {
  const widgetConfig = useOptionalWidgetConfig();
  const defaultUserConfig = getUserConfig();
  // Get fallback name immediately (non-blocking)
  const fallbackName = widgetConfig?.userDetails?.userName ?? defaultUserConfig.name;

  const [greeting, setGreeting] = useState<string>("");

  const getTimeBasedGreeting = (): string => {
    const now = new Date();
    const hour = now.getHours();

    if (hour >= 0 && hour < 12) {
      return "Good Morning";
    } else if (hour >= 12 && hour < 18) {
      return "Good Afternoon";
    } else {
      return "Good Evening";
    }
  };

  useEffect(() => {
    // Set initial greeting
    setGreeting(getTimeBasedGreeting());

    // Update greeting every minute to handle time changes
    const interval = setInterval(() => {
      setGreeting(getTimeBasedGreeting());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Current user info from the API (non-blocking, best effort). The `me` query is shared
  // with the Dashboard (one request per token, the hook picks the client) and recovers by
  // itself after a 5xx or a network failure; see useCurrentUser.
  const { data: userInfo } = useCurrentUser();

  // Use userName from API (e.g., "RUDOLPH SANFORD") once it arrives
  const userName = userInfo?.userName || fallbackName;

  return (
    <div className={`flex items-center justify-between gap-4 ${className} overflow-hidden`}>
      {/* LEFT: Greeting */}
      <div className="flex flex-col justify-center shrink-0">
        <div className="text-muted-foreground">
          {greeting} {userName},
        </div>
        <h1 className="text-foreground">Welcome to Your CareQueue</h1>
      </div>
      {/* RIGHT: Summary Cards */}
      <div className="flex gap-4 overflow-x-auto">
        {summaryData && (
          <ResidentSummaryCards
            totalCount={summaryData.totalCount}
            highRiskCount={summaryData.highRiskCount}
            mediumRiskCount={summaryData.mediumRiskCount}
            lowRiskCount={summaryData.lowRiskCount}
          />
        )}
      </div>
    </div>
  );
}
